import { useEffect, useState, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { toast } from '../components/toast';
import { getEmailsStatus } from '../services/api';
import EmailFilters from '../components/email-tracking/EmailFilters';
import EmailTrackingTable from '../components/email-tracking/EmailTrackingTable';
import EmailDetailsDialog from '../components/email-tracking/EmailDetailsDialog';

export default function EmailTrackingPage() {
  const [emails, setEmails] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [opensDetail, setOpensDetail] = useState([]);
  const [clicksDetail, setClicksDetail] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [metaData, setMetaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('send_time');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [datePreset, setDatePreset] = useState('All');

  useEffect(() => {
    const fetchEmails = async () => {
      setLoading(true);
      try {
        const response = await getEmailsStatus(startDate, endDate);
        setEmails(response.data.emails);
      } catch (error) {
        console.error('Error fetching emails:', error);
        toast.error('Failed to load emails status. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchEmails();
  }, [startDate, endDate]);

  const handleSearch = (term) => {
    if (term !== searchTerm) {
      setSearchTerm(term);
      if (page !== 0) {
        setPage(0);
      }
    }
  };

  const handleStatusFilterChange = (event) => {
    const newStatus = event.target.value;
    if (newStatus !== statusFilter) {
      setStatusFilter(newStatus);
      if (page !== 0) {
        setPage(0);
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDateRangeChange = ({ startDate, endDate, preset }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setDatePreset(preset);
    if (page !== 0) {
      setPage(0);
    }
  };

  const handleClearFilters = () => {
    if (searchTerm !== '' || statusFilter !== 'all' || startDate !== null || endDate !== null || page !== 0) {
      setSearchTerm('');
      setStatusFilter('all');
      setStartDate(null);
      setEndDate(null);
      setDatePreset('All');
      setPage(0);
    }
  };

  const filteredEmails = useMemo(() => {
    let filtered = [...emails];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (email) =>
          email.subject?.toLowerCase().includes(lowerSearchTerm) ||
          email.to?.toLowerCase().includes(lowerSearchTerm) ||
          email.metadata?.doctor_name?.toLowerCase().includes(lowerSearchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((email) => email.status.toLowerCase() === statusFilter.toLowerCase());
    }

    return filtered;
  }, [emails, searchTerm, statusFilter]);

  const handleViewDetails = (email) => {
    setOpensDetail(email.opens_detail || []);
    setClicksDetail(email.clicks_detail || []);
    setModalTitle(email.subject);
    setMetaData(email.metadata || null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setOpensDetail([]);
    setClicksDetail([]);
    setModalTitle('');
    setMetaData(null);
  };

  const uniqueStatuses = [...new Set(emails.map((email) => email.status))];

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Email Tracking</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Email Tracking
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Monitor and track all sent emails, including invoice deliveries and client communications. 
              View open rates, click-through rates, and delivery status in real-time.
            </Typography>
          </Box>

          <EmailFilters
            searchTerm={searchTerm}
            onSearch={handleSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            uniqueStatuses={uniqueStatuses}
            startDate={startDate}
            endDate={endDate}
            datePreset={datePreset}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
          />

          <EmailTrackingTable
            emails={filteredEmails}
            loading={loading}
            order={order}
            orderBy={orderBy}
            page={page}
            rowsPerPage={rowsPerPage}
            onRequestSort={handleRequestSort}
            onChangePage={(event, newPage) => setPage(newPage)}
            onChangeRowsPerPage={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            onViewDetails={handleViewDetails}
          />
        </Grid>
      </Grid>

      <EmailDetailsDialog
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalTitle}
        opensDetail={opensDetail}
        clicksDetail={clicksDetail}
        metaData={metaData}
      />
    </>
  );
}
